import { AfterViewChecked, ChangeDetectorRef, Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, switchMap, takeUntil, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { formInput, formRadioGroup, formRow, RktFormModule } from '@rocketfinancialcorp/rocket-ui/form';
import { FinancialAccountService } from '@shared/services';
import { FormSelectFieldComponent } from '@shared/components';
import { ErrorUtils, Patterns } from '@shared/utils';
import { BusinessAccount, FinancialAccountCategory, FinancialAccountListItem, FormModel } from '@shared/models';
import { businessAccountFeature } from '@store';

@Component({
  selector: 'app-add-financial-account',
  templateUrl: './add-financial-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RktFormModule, RktButtonDirective, FormSelectFieldComponent],
})
export class AddFinancialAccountComponent implements OnInit, AfterViewChecked {
  store = inject(Store);
  activeModal = inject(ActiveModal);
  ref = inject(ChangeDetectorRef);
  financialAccountService = inject(FinancialAccountService);

  createFinancialAccountForm = new FormGroup({});

  isLoading = false;

  formSubmitError!: string;

  createFinancialAccountFields = [
    formRow([
      formRadioGroup({
        key: 'category',
        label: 'Account Category',
        props: {
          placeholder: 'Select',
          options: [],
          required: true,
        },
      }),
    ]),
    formRow([
      formInput({
        key: 'name',
        label: 'Financial Account Name',
        props: {
          required: true,
          pattern: Patterns.GENERIC_NAME,
          minLength: 1,
          maxLength: 40,
        },
        validation: {
          messages: {
            required: 'Financial Account name is required',
            pattern: 'Enter a valid Financial Account Name',
          },
        },
      }),
    ]),
    formRow([
      formInput({
        key: 'nameOnAccount',
        label: 'Name on Account',
        props: { disabled: true },
      }),
    ]),
  ];

  formModel: FormModel<{ name: string; nameOnAccount: string; category: FinancialAccountCategory }> = {
    category: 'INTERNAL',
  };

  businessAccount!: BusinessAccount;

  parentFinancialAccountListItems: { label: string; value: string }[] = [];

  parentFinancialAccountListLoading = false;

  parentFinancialAccountId?: string;

  parentFinancialAccountListHasNext = true;

  parentFinancialAccountListPageNumber = 0;

  parentFinancialAccountListPageNumber$ = new Subject<number>();

  businessAccount$ = this.store.select(businessAccountFeature.selectBusinessAccountDetails).pipe(
    tap((businessAccount) => {
      if (!businessAccount) {
        return;
      }
      this.businessAccount = businessAccount;
      // operatingName (DBA) is optional; fall back to the required legalName.
      this.formModel.nameOnAccount = businessAccount.operatingName || businessAccount.legalName;
    }),
  );

  private destroy$ = new Subject<void>();

  modalInit(hasEscrowAccount: boolean): void {
    if (this.createFinancialAccountFields[0]?.fieldGroup[0]?.props) {
      this.createFinancialAccountFields[0].fieldGroup[0].props.options = [
        { value: 'INTERNAL', label: 'INTERNAL' },
        { value: 'ESCROW', label: 'ESCROW', disabled: hasEscrowAccount },
      ];
    }
  }

  ngOnInit() {
    this.parentFinancialAccountListPageNumber$
      .pipe(
        switchMap((pageNumber) => {
          this.parentFinancialAccountListLoading = true;

          return this.financialAccountService.getFinancialAccounts({
            page: pageNumber,
            size: 10,
            businessAccountId: '00000000-0000-0000-0000-000000000000',
            activeFilters: {
              state: 'ACTIVE',
              categories: 'INTEGRATED',
              internalAccountRoles: 'SETTLEMENT',
            },
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (financialAccounts) => {
          this.parentFinancialAccountListItems = this.mapParentFinancialAccountListItems(
            financialAccounts.items,
            financialAccounts.hasNext!,
          );
          this.parentFinancialAccountListLoading = false;
        },
        error: () => {
          this.parentFinancialAccountListLoading = false;
        },
      });
    this.parentFinancialAccountListPageNumber$.next(0);
  }

  mapParentFinancialAccountListItems(financialAccounts: FinancialAccountListItem[], hasNext: boolean): { label: string; value: string }[] {
    const financialAccountItems = financialAccounts.map((financialAccount) => ({
      label: `${financialAccount.name} | ***${financialAccount.bankAccount?.accountNumberTail}`,
      value: financialAccount.id,
    }));
    this.parentFinancialAccountListHasNext = hasNext;

    return [...this.parentFinancialAccountListItems, ...financialAccountItems];
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  onSubmit(): void {
    this.createFinancialAccountForm.markAllAsTouched();

    if (this.createFinancialAccountForm.invalid) {
      return;
    }

    const { category = 'INTERNAL', name, nameOnAccount } = this.formModel;

    this.isLoading = true;
    this.formSubmitError = '';

    const financialAccountData: {
      businessAccountId: string;
      name: string;
      parentId: string;
      nameOnAccount?: string;
      restrictions?: string[];
    } = {
      name: name!,
      parentId: this.parentFinancialAccountId!,
      businessAccountId: this.businessAccount.id,
    };

    if (category === 'INTERNAL') {
      financialAccountData.nameOnAccount = nameOnAccount;
      financialAccountData.restrictions = [];
    }

    this.financialAccountService
      .createFinancialAccount({ financialAccountData, category })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (createResponse) => {
          this.activeModal.close(createResponse);
          this.createFinancialAccountForm.reset();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.formSubmitError = ErrorUtils.parseExtendedError(error);
        },
      });
  }

  onParentFinancialAccountSelectScrollToEnd(): void {
    if (this.parentFinancialAccountListLoading || !this.parentFinancialAccountListHasNext) {
      return;
    }
    const pageNumber = this.parentFinancialAccountListPageNumber + 1;
    this.parentFinancialAccountListPageNumber = pageNumber;
    this.parentFinancialAccountListPageNumber$.next(pageNumber);
  }
}
